import type { FrequencyPeriodUnit, TimingCode } from "@/constants/medication-dosage";
import {
  MED_REQUEST_STATUS_ENUM_TO_FHIR_STATUS,
  TitrationDecision,
  type TitrationDecisionReason,
} from "@/constants/titration";
import type {
  BaseTitrationMedicationInput,
  MedicationRequestStatus,
  TitrationMedicationCategory,
} from "@/utils/GraphQLClient/titration";

export const GDMT_MEDICATION_CLASSES = ["rasi", "sglt2i", "nsmra", "betaBlocker"] as const;
export type GdmtMedicationClass = (typeof GDMT_MEDICATION_CLASSES)[number];

export type TitrationPlanCardStatus =
  (typeof MED_REQUEST_STATUS_ENUM_TO_FHIR_STATUS)[MedicationRequestStatus] | "empty" | "editing" | "complete";

/**
 * Form state for a single GDMT class. The dose / frequency / timing /
 * route fields are shaped to match the medications form's `DosageStep`
 * vocabulary (see `src/utils/dosage-steps.ts` + `src/constants/medication-dosage.ts`)
 * so values roundtrip 1:1 against any patient `MedicationStatement`:
 *
 * - `currentDosage.unit` / `targetDosage.unit`: a `DOSE_UNITS.value` (e.g. "mg", "tablet").
 * - `frequency`: a `FREQUENCY_PRESETS.value` (e.g. "once_daily", "as_needed", "custom").
 * - `frequencyTimes` / `frequencyEvery` / `frequencyUnit`: filled only when
 *   `frequency === "custom"` (mirrors the editor's custom-frequency triple).
 * - `timing`: an array of EventTiming codes (e.g. "MORN", "HS", "AC").
 * - `route`: free-text — matches the medications form's `dosage[].route.text`
 *   convention.
 */

/**
 * A dose amount. A single dose populates `low` only (e.g. `{ low: 20 }`); a
 * combination / range dose populates both bounds (e.g. `{ low: 20, high: 15 }`,
 * displayed as "20/15"). An empty value is `{}` (both `undefined`).
 */
export interface DosageValue {
  /** Dose amount, or the low bound of a combination/range dose. */
  low?: number;
  /** High bound — present only for combination/range doses. */
  high?: number;
}

export interface Dosage {
  value: DosageValue;
  unit: string;
  frequency: string;
  /** "Times" part of a custom frequency (e.g. 3 in "3 times every 2 days"). Filled only when frequency === "custom". */
  frequencyTimes: string;
  /** "Every" part of a custom frequency (e.g. 2 in "3 times every 2 days"). Filled only when frequency === "custom". */
  frequencyEvery: string;
  /** Period unit for a custom frequency — one of `FREQUENCY_PERIOD_UNITS.value` ("h" / "d" / "wk" / "mo"). Empty string until the practitioner picks one. */
  frequencyUnit: FrequencyPeriodUnit | "";
  /** EventTiming codes ("MORN", "AFT", "EVE", "HS", "AC", "PC"). Multi-select. */
  timing: TimingCode[];
}

/** True when a dose value carries an amount. */
export const dosageValueHasContent = (value: DosageValue): boolean => value.low !== undefined;

/** Structural equality for two dose values. */
export const dosageValueEquals = (a: DosageValue, b: DosageValue): boolean => a.low === b.low && a.high === b.high;

/**
 * Render a dose value for display / text inputs: a range as "low/high"
 * (e.g. "20/15"), a single dose as "low", an empty value as "".
 */
export const formatDosageValue = (value: DosageValue): string => {
  if (value.low === undefined) {
    return "";
  }
  return value.high !== undefined ? `${value.low}/${value.high}` : `${value.low}`;
};

/**
 * Parse a text-input dose string into a `DosageValue`. Accepts a single number
 * ("20") or two numbers separated by a slash ("20/15" for combination drugs).
 * Blank / non-numeric components become `undefined`.
 */
export const parseDosageValue = (input: string): DosageValue => {
  const [lowRaw = "", highRaw = ""] = input.split("/");
  const low = Number(lowRaw.trim());
  const high = Number(highRaw.trim());
  return {
    low: lowRaw.trim() !== "" && Number.isFinite(low) ? low : undefined,
    high: highRaw.trim() !== "" && Number.isFinite(high) ? high : undefined,
  };
};

/**
 * Collapse a dose value to a single comparable number — the sum of its bounds.
 * Combination doses escalate proportionally, so the sum preserves ordering.
 * Returns `NaN` for an empty value.
 */
export const dosageValueToNumber = (value: DosageValue): number =>
  value.low === undefined ? NaN : value.low + (value.high ?? 0);

export interface TitrationPlanEntry {
  status: TitrationPlanCardStatus;
  collapsed: boolean;
  /**
   * Identifies the selected medication source:
   * - empty string when nothing picked,
   * - `CUSTOM_MEDICATION_ID` when the practitioner is entering a new
   *   medication from scratch,
   * - the FHIR `MedicationStatement.id` when picked from the patient's
   *   active GDMT meds (this same id flows to the BE as
   *   `source_medication_statement_id`).
   */
  medicationId: string;
  /** Display name for the medication — populated from MS on pick, or typed by the user for the custom path. */
  medicationName: string;
  /**
   * RASi sub-category (`acei` / `arbs` / `arnis`). Only applicable to the RASi class;
   * `undefined` until picked. Flows to the BE as the `createTitrationPlan` category param.
   */
  medicationCategory: TitrationMedicationCategory | undefined;
  /** Free-text route — same convention as the medications form (`dosage[].route.text`). */
  route: string;
  /* MedicationRequest.dispenseRequest.validityPeriod.start => Date med was started */
  dateStarted: string;
  /* MedicationRequest.note[].text =>  If 'Other' is selected for the decision reason, there is an input for additional notes */
  reason: string;
  /** MedicationRequest.extension[].valueCodeableConcept => Increase/Decrease/Hold/Continue/Discontinue medication for next plan */
  decision?: TitrationDecision;
  /** MedicationRequest.extension[].valueCodeableConcept => Reasons behind the titration decision. Multi-select. */
  decisionReasons: TitrationDecisionReason[];
  // Current titration plan's dosage - readonly when replacing plan
  currentDosage: Dosage;
  // Incoming titration plan that will replace existing one's dosage - value that changes when replacing plan
  newDosage: Dosage;
  // Target dose of medication
  targetDosage: Dosage;
  /* If medication is being replaced, add to `medications.discontinued_medication` field.
   */
  /** Is medication being replaced?
   * - YES: Add discont'd med to `medications.discontinued_medication` field
   *      - ReplacED med (discont'd): Use planState.medRequestMap to populate fields (target, med name, etc.)
   *      - ReplacING med (incoming): Use form values to populate fields
   * - NO: Proceed as normal
   */
  isReplacingMedication?: boolean;
  /** Fields needed to populate discontinued, replaced medication
   * - Other fields can be retrieved from TitrationPlanContext.medRequestMap (medication_codeable_concept, dosage, target dose)
   */
  replacedMedication?: Pick<
    BaseTitrationMedicationInput,
    "decision" | "decision_reasons" | "reason_for_titration" | "source_medication_statement_id"
  >;
  // Medication form (ex. capsule, pill, puff, etc) - refer to FORM_UNITS
  form: string;
  // # of medication (2 pills, 3 puffs, etc.)
  quantity?: number;
}

export type TitrationPlanState = Record<GdmtMedicationClass, TitrationPlanEntry>;

export const CUSTOM_MEDICATION_ID = "__custom__";
